import React from 'react';
import { Box, Container, Grid, Typography, TextField, Button, IconButton, Chip } from '@mui/material';
import { useTheme } from '../../../ThemeContext';
import { Email, Phone, LocationOn, AccessTime } from '@mui/icons-material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SendIcon from '@mui/icons-material/Send';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CreateIcon from '@mui/icons-material/Create';

const infoItems = [
  { icon: <LocationOn sx={{ color: '#07a4f5' }} />, label: 'Office', text: '2nd Floor, 1A, MAIN ROAD, 3, Mysore Rd, near Indian Oil Petrol Pump, Chaitra Layout, Nayandahalli, Bengaluru, Karnataka 560039' },
  { icon: <Phone sx={{ color: '#07a4f5' }} />, label: 'Phone', text: '+91 9629757655\nWe\'re available to assist you.' },
  { icon: <AccessTime sx={{ color: '#07a4f5' }} />, label: 'Work Hours', text: 'Monday - Saturday\n09:30 AM - 06:30 PM\nSunday Closed' },
  { icon: <Email sx={{ color: '#07a4f5' }} />, label: 'Email', text: 'support@filmhookapps.com\nDrop us an email anytime!' },
];

const socialIcons = [
  { icon: <FacebookIcon fontSize="small" />, color: '#1877f2' },
  { icon: <InstagramIcon fontSize="small" />, color: '#e1306c' },
  { icon: <WhatsAppIcon fontSize="small" />, color: '#25d366' },
  { icon: <YouTubeIcon fontSize="small" />, color: '#ff0000' },
  { icon: <LinkedInIcon fontSize="small" />, color: '#0077b5' },
];

const ContactDetails = () => {
  const { isDarkMode } = useTheme();

  const bgPrimary = isDarkMode ? '#050a14' : '#f8fafc';
  const cardBg = isDarkMode ? '#101726' : '#ffffff';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  return (
    <Box sx={{ pt: { xs: 8, md: 12 }, pb: 4, px: { xs: 2, md: 6 }, background: bgPrimary }}>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 4, md: 6 }, alignItems: 'flex-start' }}>
          
          {/* Left Column (Form) */}
        <Box sx={{ width: { xs: '100%', md: '41.666%', lg: '33.333%' }, flexShrink: 0 }}>
          <Box
            sx={{
              background: cardBg,
              borderRadius: '24px',
              p: { xs: 3, md: 4 },
              boxShadow: isDarkMode ? '0 16px 48px rgba(0,0,0,0.4)' : '0 16px 48px rgba(7, 164, 245,0.08)',
              border: `1px solid ${borderColor}`,
              mb: 4
            }}
          >
            <Typography variant="h5" fontWeight={800} mb={4} sx={{ color: textPrimary }}>
              Leave your message
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              <TextField
                fullWidth
                placeholder="Your name"
                variant="outlined"
                InputProps={{
                  startAdornment: <PersonOutlineIcon sx={{ color: '#07a4f5', mr: 1.5 }} />,
                  sx: { color: textPrimary, borderRadius: '12px', '& fieldset': { borderColor: borderColor }, '&:hover fieldset': { borderColor: 'rgba(7, 164, 245,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#07a4f5' } }
                }}
              />
              
              <TextField
                fullWidth
                placeholder="Your email"
                variant="outlined"
                InputProps={{
                  startAdornment: <MailOutlineIcon sx={{ color: '#07a4f5', mr: 1.5 }} />,
                  sx: { color: textPrimary, borderRadius: '12px', '& fieldset': { borderColor: borderColor }, '&:hover fieldset': { borderColor: 'rgba(7, 164, 245,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#07a4f5' } }
                }}
              />
              
              <TextField
                fullWidth
                placeholder="Your message"
                variant="outlined"
                multiline
                rows={4}
                InputProps={{
                  startAdornment: <Box sx={{ height: '100%', pt: 1, display: 'flex', alignItems: 'flex-start' }}><CreateIcon sx={{ color: '#07a4f5', mr: 1.5 }} /></Box>,
                  sx: { color: textPrimary, borderRadius: '12px', '& fieldset': { borderColor: borderColor }, '&:hover fieldset': { borderColor: 'rgba(7, 164, 245,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#07a4f5' } }
                }}
              />
              
              <Button
                fullWidth
                variant="contained"
                endIcon={<SendIcon />}
                sx={{
                  background: '#07a4f5',
                  color: 'white',
                  fontWeight: 700,
                  borderRadius: '12px',
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 8px 20px rgba(7, 164, 245,0.3)',
                  '&:hover': { background: '#068bc2', boxShadow: '0 8px 24px rgba(7, 164, 245,0.5)' },
                }}
              >
                Send Message
              </Button>
            </Box>
          </Box>

          {/* Social Media */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography fontWeight={800} sx={{ color: textPrimary }}>
              Follow us on Social Media
            </Typography>
            <Box display="flex" gap={1}>
              {socialIcons.map((s, i) => (
                <IconButton
                  key={i}
                  size="small"
                  sx={{
                    background: cardBg,
                    border: `1px solid ${borderColor}`,
                    color: textPrimary,
                    borderRadius: '50%',
                    transition: 'all 0.3s',
                    '&:hover': { background: s.color, color: 'white', border: `1px solid ${s.color}`, transform: 'scale(1.1)' },
                  }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right Column (Info) */}
        <Box sx={{ flexGrow: 1, width: { xs: '100%', md: '58.333%', lg: '66.666%' }, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 6, alignItems: 'center' }}>
            
            {/* Text */}
            <Box sx={{ flex: 1 }}>
              <Chip
                label="GET IN TOUCH"
                size="small"
                sx={{ background: isDarkMode ? 'rgba(7, 164, 245,0.1)' : '#e0f2fe', color: '#07a4f5', fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.05em', mb: 2, borderRadius: '8px' }}
              />
              <Typography variant="h2" fontWeight={900} sx={{ color: textPrimary, fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 2, lineHeight: 1.1 }}>
                Don't hesitate to <Box component="span" sx={{ color: '#07a4f5' }}>contact us</Box>
              </Typography>
              <Typography variant="body1" sx={{ color: textSecondary, lineHeight: 1.8, fontSize: '1rem', maxWidth: '90%' }}>
                At Filmhook Mediaapps Pvt Ltd, we are committed to providing exceptional services. If you have any inquiries or need assistance, don't hesitate to reach out!
              </Typography>
            </Box>

            {/* 3D Image */}
            <Box sx={{ flex: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Box 
                component="img" 
                src="https://images.unsplash.com/photo-1577563908411-5077b6dc7624?auto=format&fit=crop&w=600&q=80" 
                alt="Contact us 3D" 
                sx={{ width: '100%', maxWidth: 400, borderRadius: '24px', boxShadow: isDarkMode ? '0 16px 48px rgba(0,0,0,0.4)' : '0 16px 48px rgba(7, 164, 245,0.1)', objectFit: 'cover', aspectRatio: '4/3' }}
              />
            </Box>

          </Box>

          {/* Info Cards */}
          <Grid container spacing={2}>
            {infoItems.map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box
                  sx={{
                    background: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '20px',
                    p: 3,
                    height: '100%',
                    boxShadow: isDarkMode ? 'none' : '0 4px 12px rgba(0,0,0,0.02)',
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.3)' : '0 8px 24px rgba(7, 164, 245,0.1)', border: `1px solid rgba(7, 164, 245,0.3)` },
                  }}
                >
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: isDarkMode ? 'rgba(7, 164, 245,0.1)' : '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    {item.icon}
                  </Box>
                  <Typography fontWeight={800} sx={{ color: textPrimary, fontSize: '0.95rem', mb: 1.5 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: textSecondary, lineHeight: 1.6, whiteSpace: 'pre-line', fontSize: '0.8rem' }}>
                    {item.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          
        </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactDetails;
